import { useStructuredWorkflow } from "@/api/workflow-hooks";
import {
  FileSearch,
  Globe,
  Keyboard,
  MousePointerClick,
  ScrollText,
} from "lucide-react";
import type React from "react";
import { useCallback } from "react";
import { Handle, Position } from "reactflow";
import TextEditor from "../../TextEditor";

export type ActionType = "Click" | "Type" | "Navigate" | "Extract" | "Scroll";

interface RPANodeProps {
  data: {
    label: string;
    actionType: ActionType;
    prompt: string;
    url?: string;
    selector?: string;
    text?: string;
    forceComputerUse?: boolean;
    onPromptChange?: (update: unknown) => void;
    onUrlChange?: (url: string) => void;
    onSelectorChange?: (selector: string) => void;
    onTextChange?: (text: string) => void;
    onLabelChange?: (label: string) => void;
    onForceComputerUseChange?: (force: boolean) => void;
    workflowId: string;
  };
  isConnectable: boolean;
}

const RPANode: React.FC<RPANodeProps> = ({ data, isConnectable }) => {
  const { variableStrings } = useStructuredWorkflow(data.workflowId);

  const handlePromptChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      data.onPromptChange?.(event.target.value);
    },
    [data],
  );

  const handleLabelChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      data.onLabelChange?.(event.target.value);
    },
    [data],
  );

  const handleForceComputerUseChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      data.onForceComputerUseChange?.(event.target.checked);
    },
    [data],
  );

  const getIcon = (type: ActionType) => {
    switch (type) {
      case "Click":
        return <MousePointerClick size={16} />;
      case "Type":
        return <Keyboard size={16} />;
      case "Navigate":
        return <Globe size={16} />;
      case "Extract":
        return <FileSearch size={16} />;
      case "Scroll":
        return <ScrollText size={16} />;
    }
  };
  const getBackgroundColor = (type: ActionType) => {
    switch (type) {
      case "Click":
        return "#B4D9FF"; // Deeper soft blue
      case "Type":
        return "#E1F0FF"; // Light sky blue
      case "Navigate":
        return "#C2D1FF"; // Periwinkle blue
      case "Extract":
        return "#E9E3FF"; // Light purple
      case "Scroll":
        return "#D0F0FF"; // Bright cyan blue
      default:
        return "#FFFFFF";
    }
  };

  return (
    <div
      className="rpa-node"
      style={{
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        padding: "0",
        borderRadius: "8px",
        backgroundColor: "white",
        fontFamily:
          'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
      />
      <div
        className="rpa-node-header"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          backgroundColor: getBackgroundColor(data.actionType),
          padding: "12px",
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <span style={{ display: "flex", alignItems: "center" }}>
          {getIcon(data.actionType)}
        </span>
        <input
          type="text"
          value={data.label}
          onChange={handleLabelChange}
          style={{
            backgroundColor: "transparent",
            border: "none",
            color: "#111",
            fontWeight: "600",
            outline: "none",
            width: "100%",
            fontSize: "14px",
            fontFamily: "inherit",
          }}
        />
      </div>
      <div className="rpa-node-body" style={{ padding: "12px" }}>
        {/* <textarea
          className="prompt-input"
          value={data.prompt}
          onChange={handlePromptChange}
          placeholder={`Enter instructions for ${data.actionType.toLowerCase()} action...`}
          style={{
            width: "100%",
            minHeight: "150px",
            padding: "12px",
            border: "1px solid #eaeaea",
            borderRadius: "6px",
            resize: "none",
            fontFamily: "inherit",
            fontSize: "14px",
            lineHeight: "1.6",
            outline: "none",
            transition: "border-color 0.15s ease",
            boxSizing: "border-box",
            color: "#333",
          }}
          onFocus={(e) => (e.target.style.borderColor = "#000")}
          onBlur={(e) => (e.target.style.borderColor = "#eaeaea")}
        /> */}
        <TextEditor
          content={data.prompt}
          onChange={(update) => {
            data.onPromptChange?.(update);
          }}
          placeholder={`Enter instructions for ${data.actionType.toLowerCase()} action...`}
          extractions={variableStrings}
          // Need `nodrag` to let mouse events be captured by tiptap inside the node
          className="border rounded nodrag"
          editorClassName="min-h-[150px]"
        />
        <div
          style={{
            marginTop: "12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <input
            type="checkbox"
            id={`force-computer-use-${data.label}`}
            checked={data.forceComputerUse || false}
            onChange={handleForceComputerUseChange}
            style={{ cursor: "pointer" }}
          />
          <label
            htmlFor={`force-computer-use-${data.label}`}
            style={{
              fontSize: "14px",
              color: "#666",
              cursor: "pointer",
            }}
          >
            Force Computer Use
          </label>
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={isConnectable}
      />
    </div>
  );
};

export default RPANode;
